import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';

import { logException } from '#/features/logging/logging';
import { prepareErrorMessage } from '#/features/wallet/ethereum';
import { translator } from '#/features/localization/utils';

import createSafeContext from '#/utils/createSafeContext';
import { AsyncResult } from '#/utils/types';

import useInitiateWithdraw from './useInitiateWithdraw';

export interface WithdrawState {
  readonly status: 'idle' | 'busy';
  readonly error: string;
}

const INITIAL_STATE: WithdrawState = {
  status: 'idle',
  error: '',
};

export interface WithdrawView {
  readonly isBusy: boolean;
  readonly step: string;
  readonly error: string;
}

export interface WithdrawActions {
  readonly initiateWithdraw: (
    amount: string,
    asset: string,
  ) => AsyncResult<void>;
}

export const [useWithdrawState, WithdrawStateCtx] =
  createSafeContext<WithdrawState>('WithdrawState');
export const [useWithdrawView, WithdrawViewCtx] =
  createSafeContext<WithdrawView>('WithdrawView');
export const [useWithdrawActions, WithdrawActionsCtx] =
  createSafeContext<WithdrawActions>('WithdrawActions');

interface ProviderProps {
  readonly children: ReactNode;
}

export function WithdrawProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);
  const view = useMemo(() => createView(state), [state]);

  const initiateWithdraw = useInitiateWithdraw();
  const actions = useMemo(
    () => createActions(setState, initiateWithdraw),
    [setState, initiateWithdraw],
  );

  return (
    <WithdrawStateCtx value={state}>
      <WithdrawViewCtx value={view}>
        <WithdrawActionsCtx value={actions}>
          {props.children}
        </WithdrawActionsCtx>
      </WithdrawViewCtx>
    </WithdrawStateCtx>
  );
}

function createView(state: WithdrawState): WithdrawView {
  const isBusy = state.status !== 'idle';
  const step = isBusy
    ? translator.t('Withdraw initiated')
    : translator.t('Withdraw');
  return { isBusy, step, error: state.error };
}

function createActions(
  setState: Dispatch<SetStateAction<WithdrawState>>,
  _initiateWithdraw: (amount: string, asset: string) => AsyncResult<void>,
): WithdrawActions {
  function setBusy() {
    setState((state) => ({ ...state, status: 'busy', error: '' }));
  }

  function setSuccess() {
    setState((state) => ({ ...state, status: 'idle', error: '' }));
  }

  const setFailure = (error: string) => {
    setState((state) => ({ ...state, status: 'idle', error }));
  };

  const initiateWithdraw = async (
    amount: string,
    asset: string,
  ): AsyncResult<void> => {
    try {
      setBusy();
      const result = await _initiateWithdraw(amount, asset);
      if (!result.ok) {
        setFailure(result.reason ?? 'Unknown error');
      } else {
        setSuccess();
      }
      return result;
    } catch (err) {
      const description = 'Failed to initiate withdraw';
      const { message } = prepareErrorMessage(description, err);
      logException(new Error(description, { cause: err }));
      setFailure(message);
      return { ok: false, error: null };
    }
  };

  return { initiateWithdraw };
}
